// src/ArticleCpdModule.js

import React, { useState, useEffect } from 'react';
import { CheckCircle, Edit3, Loader, Save } from 'lucide-react';

const ArticleCpdModule = ({ article, user, onLoginRequest, onLogCpd, cpdLog }) => {
    const [reflection, setReflection] = useState('');
    const [answers, setAnswers] = useState({});
    const [reflectionAnswers, setReflectionAnswers] = useState({});
    const [view, setView] = useState('start'); // 'start', 'quiz', 'reflection', 'completed'
    const [status, setStatus] = useState('');
    
    // --- MODIFICATION START ---
    // 1. New state for the verification checkbox
    const [hasReadArticle, setHasReadArticle] = useState(false);
    // --- MODIFICATION END ---

    useEffect(() => {
        if (cpdLog) {
            setView('completed');
            setReflection(cpdLog.reflection || '');
        }
    }, [cpdLog]);

    useEffect(() => {
        if (view !== 'reflection' || !article.reflectionHelper) return;

        const generatedText = article.reflectionHelper.questions.map((q, index) => {
            const answerText = reflectionAnswers[index];
            if (answerText) {
                return `${q.question}\n- ${answerText}\n`;
            }
            return '';
        }).filter(Boolean).join('\n');
        
        setReflection(generatedText + "\nHow this will change my practice:\n- ");

    }, [reflectionAnswers, article.reflectionHelper, view]);


    if (!user || user.isAnonymous) {
        return (
            <div className="mt-6 p-4 bg-gray-200 dark:bg-gray-800 rounded-lg text-center">
                <p className="font-semibold text-gray-700 dark:text-gray-300">Want to log this for CPD?</p>
                <button onClick={onLoginRequest} className="mt-2 text-blue-600 dark:text-blue-400 font-semibold hover:underline">
                    Login or Sign Up to start your CPD log.
                </button>
            </div>
        );
    }
    
    const handleSubmitQuiz = (e) => {
        e.preventDefault();
        if (Object.keys(answers).length === article.quiz.questions.length) {
            setView('reflection');
        } else {
            alert("Please answer all questions to proceed.");
        }
    };

    const handleSaveLog = async () => {
        setStatus('saving');
        const learningGoal = `To update my knowledge on ${article.tags[0] || 'SABR'} based on the findings of "${article.title}".`;
        
        try {
            await onLogCpd({
                articleId: article.id,
                articleTitle: article.title,
                journal: article.journal,
                year: article.year,
                articleLink: article.link,
                learningGoal: learningGoal,
                reflection: reflection,
                activityDetails: `Reviewed AI summary and key findings for article: "${article.title}", ${article.journal} (${article.year}).`,
                // --- MODIFICATION START ---
                // 2. Changed CPD hours from 0.25 to 0.5
                hours: 0.5,
                // --- MODIFICATION END ---
            });
            setStatus('success');
            setTimeout(() => setView('completed'), 1500);
        } catch (error) {
            setStatus('error');
            console.error("Failed to save CPD Log:", error);
        }
    };

    const handleReflectionAnswerChange = (qIndex, answer) => {
        setReflectionAnswers(prev => ({ ...prev, [qIndex]: answer }));
    };

    if (view === 'completed') {
         return (
             <div className="mt-6 p-4 bg-green-100 dark:bg-green-900/20 rounded-lg border border-green-300 dark:border-green-800">
                <div className="flex items-center text-green-800 dark:text-green-300 font-bold">
                    <CheckCircle size={20} className="mr-2"/>
                    <p>Completed & Logged for CPD</p>
                </div>
            </div>
         );
    }

    return (
        <div className="mt-6 p-4 bg-blue-50 dark:bg-gray-900/30 rounded-lg border border-blue-200 dark:border-gray-700">
            <h4 className="font-bold text-lg text-blue-800 dark:text-blue-300 mb-3">AHPRA CPD Module</h4>
            
            {view === 'start' && (
                <button onClick={() => setView('quiz')} className="w-full py-2 px-4 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 flex items-center justify-center">
                    <Edit3 size={18} className="mr-2"/>
                    Start CPD Module
                </button>
            )}

            {view === 'quiz' && (
                <form onSubmit={handleSubmitQuiz}>
                    <h5 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">1. Test Your Understanding</h5>
                    {article.quiz.questions.map((q, qIndex) => (
                        <div key={qIndex} className="mb-3">
                            <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">{q.questionText}</p>
                            <div className="flex flex-col space-y-1 mt-1">
                                {q.options.map((opt, oIndex) => (
                                    <label key={oIndex} className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                                        <input type="radio" name={`q${qIndex}`} required onChange={() => setAnswers(prev => ({...prev, [qIndex]: oIndex}))} className="form-radio text-blue-600 focus:ring-blue-500"/>
                                        <span>{opt}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                    <button type="submit" className="w-full mt-2 py-2 px-4 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700">
                        Continue to Reflection
                    </button>
                </form>
            )}

            {view === 'reflection' && (
                <div>
                    <h5 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">2. Guide Your Reflection</h5>
                     {article.reflectionHelper && article.reflectionHelper.questions.map((q, qIndex) => (
                        <div key={qIndex} className="mb-3">
                            <label className="block text-sm text-gray-700 dark:text-gray-300 font-medium mb-1">{q.question}</label>
                            <select onChange={(e) => handleReflectionAnswerChange(qIndex, e.target.value)} className="w-full p-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                                <option value="">Select an option...</option>
                                {q.options.map((opt, oIndex) => <option key={oIndex} value={opt}>{opt}</option>)}
                            </select>
                        </div>
                     ))}
                    
                    <div className="mt-4">
                        <label className="block text-md font-medium text-gray-700 dark:text-gray-300 mb-1">3. Finalise & Save Reflection</label>
                        <textarea
                            className="w-full h-40 p-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200 border border-gray-300 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={reflection}
                            onChange={(e) => setReflection(e.target.value)}
                        />
                    </div>
                    
                    {/* --- MODIFICATION START --- */}
                    {/* 3. Add the verification checkbox */}
                    <div className="mt-4 flex items-center">
                        <input
                            type="checkbox"
                            id="article-read-verification"
                            checked={hasReadArticle}
                            onChange={(e) => setHasReadArticle(e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="article-read-verification" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                            I verify I have read the full article text.
                        </label>
                    </div>
                    {/* --- MODIFICATION END --- */}

                    <button
                        onClick={handleSaveLog}
                        // 4. Update the disabled logic to include the new checkbox state
                        disabled={!reflection || status === 'saving' || !hasReadArticle}
                        className="w-full mt-2 py-2 px-4 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                       {status === 'saving' ? <Loader size={18} className="animate-spin mr-2"/> : <Save size={18} className="mr-2" />}
                       {status === 'saving' ? 'Saving...' : 'Save to CPD Log'}
                    </button>
                    {status === 'success' && <p className="text-sm text-green-600 dark:text-green-400 mt-2 text-center">Log saved successfully!</p>}
                    {status === 'error' && <p className="text-sm text-red-600 dark:text-red-400 mt-2 text-center">Could not save log. Please try again.</p>}
                </div>
            )}
        </div>
    );
}

export default ArticleCpdModule;